import { AnimationEngine } from './AnimationEngine';
import { AnimationModeConfig } from './AnimationTypes';
import { DeviceProfile } from '@/components/provider/UserCurrentProvider';
import { notify } from '@/utils';

export class AnimationController {
    public engine: AnimationEngine;
    private uniqueId: number;
    private activePlaybackMode: AnimationModeConfig;
    private frames: any[];

    // Vanilla DOM Caches
    public container: HTMLElement | null = null;
    public playBtn: HTMLElement | null = null;
    public prevBtn: HTMLElement | null = null;
    public nextBtn: HTMLElement | null = null;
    public fpsBtn: HTMLElement | null = null;
    public qualityBtn: HTMLElement | null = null;
    public fpsPopup: HTMLElement | null = null;
    public qualityPopup: HTMLElement | null = null;
    public controlsHUD: HTMLElement | null = null;
    public playTooltip: HTMLElement | null = null;
    public progressBar: HTMLElement | null = null;
    public editInfo: HTMLElement | null = null;
    public editInfoText: HTMLElement | null = null;
    public editInfoTooltip: HTMLElement | null = null;
    public userWantsToPlay: boolean = false;
    public settingsMobileTrigger: HTMLElement | null = null;
    public bottomSheet: HTMLElement | null = null;
    public bottomSheetClose: HTMLElement | null = null;
    public mobilePlayOverlayBtn: HTMLElement | null = null;
    public focusBtn: HTMLElement | null = null;
    public focusPopup: HTMLElement | null = null;
    public bsFocusPanel: HTMLElement | null = null;

    // Timeout Trackers
    public hideTimeout: ReturnType<typeof setTimeout> | null = null;
    public engineTrackerInterval: ReturnType<typeof setInterval> | null = null;

    // Gestures Tracker
    public isMouseOverControls = false;

    // Bottom Sheet Internal Layout Navigation Roots
    public bsMainPanel: HTMLElement | null = null;
    public bsFpsPanel: HTMLElement | null = null;
    public bsQualityPanel: HTMLElement | null = null;

    // Interactive True Hover Tracking Flag Configuration
    private hasTrueHover: boolean = true;
    private touchStartX = 0;

    constructor(config: {
        uniqueId: number;
        frames: any[];
        subDir: string;
        activePlaybackMode: AnimationModeConfig;
        deviceProfile: DeviceProfile;
        w: number;
        h: number;
        alignmentIndex?: number;
    }) {
        this.uniqueId = config.uniqueId;
        this.frames = config.frames;
        this.activePlaybackMode = config.activePlaybackMode;
        this.hasTrueHover = config.deviceProfile?.hasTrueHover ?? true;
        this.engine = new AnimationEngine({
            nodeId: config.uniqueId,
            frames: config.frames,
            subDir: config.subDir,
            playbackMode: config.activePlaybackMode,
            fallbackStrategy:
                config.activePlaybackMode.fallbackStrategy || 'wait',
            deviceProfile: config.deviceProfile,
            sourceWidth: config.w,
            sourceHeight: config.h,
            alignmentIndex: config.alignmentIndex,
        });
    }

    public cacheDOMElements(): void {
        const id = this.uniqueId;
        this.container = document.getElementById(`AnimationContainer_${id}`);
        this.playBtn = document.getElementById(`PlayToggle_${id}`);
        this.prevBtn = document.getElementById(`PrevFrame_${id}`);
        this.nextBtn = document.getElementById(`NextFrame_${id}`);
        this.fpsBtn = document.getElementById(`FpsCtrl_${id}`);
        this.qualityBtn = document.getElementById(`QualityCtrl_${id}`);
        this.fpsPopup = document.getElementById(`FpsPopup_${id}`);
        this.qualityPopup = document.getElementById(`QualityPopup_${id}`);

        this.focusBtn = document.getElementById(`FocusCtrl_${id}`);
        this.focusPopup = document.getElementById(`FocusPopup_${id}`);
        this.bsFocusPanel = document.getElementById(
            `BottomSheetFocusPanel_${id}`,
        );

        this.controlsHUD = document.getElementById(`AnimationControls_${id}`);
        this.playTooltip = document.getElementById(
            `PlayToggleHudTooltip_${id}`,
        );
        this.progressBar = document.getElementById(
            `AnimationProgressBar_${id}`,
        );
        this.editInfoText = document.getElementById(`EditInfoText_${id}`);
        this.editInfo = document.getElementById(`EditInfo_${id}`);
        this.editInfoTooltip = document.getElementById(`EditInfoTooltip_${id}`);
        // New Native Mobile DOM Node Discoveries Cache Assignments
        this.settingsMobileTrigger = document.getElementById(
            `SettingsMobileTrigger_${id}`,
        );
        this.bottomSheet = document.getElementById(`BottomSheet_${id}`);
        this.bottomSheetClose = document.getElementById(
            `BottomSheetClose_${id}`,
        );
        this.mobilePlayOverlayBtn = document.getElementById(
            `MobilePlayOverlayBtn_${id}`,
        );

        this.bsMainPanel = document.getElementById(
            `BottomSheetMainPanel_${id}`,
        );
        this.bsFpsPanel = document.getElementById(`BottomSheetFpsPanel_${id}`);
        this.bsQualityPanel = document.getElementById(
            `BottomSheetQualityPanel_${id}`,
        );
    }

    public resetHUDTimer = (): void => {
        if (this.container) this.container.classList.add('isHudVisible');
        if (
            this.progressBar &&
            this.progressBar.classList.contains('isLoaded')
        ) {
            this.progressBar.classList.add('isVisible');
        }

        if (this.hideTimeout) clearTimeout(this.hideTimeout);
        if (this.isMouseOverControls) return;

        // Guard checking layout visibility states before starting fade schedules
        const isAnyPopupActive =
            (this.fpsPopup && this.fpsPopup.classList.contains('isOpen')) ||
            (this.qualityPopup &&
                this.qualityPopup.classList.contains('isOpen')) ||
            (this.bottomSheet && this.bottomSheet.classList.contains('isOpen'));

        if (isAnyPopupActive) return;

        // CONTEXT FIX: Completely disable the 2-second auto-fade on ALL touch-first screens
        if (!this.hasTrueHover) return;

        this.hideTimeout = setTimeout(() => {
            if (this.container) this.container.classList.remove('isHudVisible');
            if (
                this.progressBar &&
                this.progressBar.classList.contains('isLoaded')
            ) {
                this.progressBar.classList.remove('isVisible');
            }
        }, 2000);
    };

    public handleToggle = (): void => {
        const isPlaying = this.engine.togglePlay();

        this.userWantsToPlay = isPlaying;

        if (this.mobilePlayOverlayBtn) {
            if (isPlaying) {
                this.mobilePlayOverlayBtn.innerHTML =
                    '<span class="Icon Icon-pause"></span>';
            } else {
                this.mobilePlayOverlayBtn.innerHTML =
                    '<span class="Icon Icon-caret"></span>';
            }
        }

        if (this.playBtn) {
            if (isPlaying) {
                this.playBtn.classList.add('isPlaying');
                this.playBtn.innerHTML =
                    '<span class="Icon Icon-pause"></span>';
            } else {
                this.playBtn.classList.remove('isPlaying');
                this.playBtn.innerHTML =
                    '<span class="Icon Icon-caret"></span>';
            }
        }

        if (this.playTooltip) {
            this.playTooltip.textContent = isPlaying
                ? 'Pausieren'
                : 'Abspielen';
        }

        const buttonPair = document.getElementById(
            `ButtonPair_${this.uniqueId}`,
        );
        if (buttonPair && this.activePlaybackMode.modeType !== 'SLIDESHOW') {
            buttonPair.style.display = isPlaying ? 'none' : 'flex';
        }
    };

    public handlePrev = (): void => {
        this.engine.stepFrame(-1);
    };

    public handleNext = (): void => {
        this.engine.stepFrame(1);
    };

    public startTrackerInterval(): void {
        this.engineTrackerInterval = setInterval(() => {
            const engineRaw = this.engine as any;
            const currentFrame =
                (engineRaw.currentIndex !== undefined
                    ? engineRaw.currentIndex
                    : 0) + 1;
            const totalFrames = this.frames.length;
            const loadedCount = engineRaw.loadedCount || currentFrame;

            if (this.progressBar) {
                const fillNode = this.progressBar.querySelector(
                    '.Animation_progression',
                ) as HTMLElement;

                if (!this.progressBar.classList.contains('isLoaded')) {
                    const loadingPercent = (loadedCount / totalFrames) * 100;
                    if (fillNode) fillNode.style.width = `${loadingPercent}%`;

                    if (loadedCount >= totalFrames) {
                        this.progressBar.classList.add('isLoaded');

                        if (
                            this.container &&
                            this.container.classList.contains('isHudVisible')
                        ) {
                            this.progressBar.classList.add('isVisible');
                        }
                    }
                } else {
                    const playbackPercent = (currentFrame / totalFrames) * 100;
                    if (fillNode) fillNode.style.width = `${playbackPercent}%`;
                }
            }

            if (this.editInfoText?.innerText && this.frames[currentFrame - 1]) {
                this.editInfoText.innerText = `${this.frames[currentFrame - 1].frameStem} | ${currentFrame}/${totalFrames}`;
            }
        }, 60);
    }

    public handleDocumentClickOutside = (e: MouseEvent): void => {
        const target = e.target as HTMLElement;
        const clickedOutsideControls =
            !target.closest('.Animation_controls') &&
            !target.closest('.Animation_popup');

        if (clickedOutsideControls) {
            let popupsClosed = false;
            if (
                this.focusPopup &&
                this.focusPopup.classList.contains('isOpen')
            ) {
                this.focusPopup.classList.remove('isOpen');
                popupsClosed = true;
            }
            if (this.fpsPopup && this.fpsPopup.classList.contains('isOpen')) {
                this.fpsPopup.classList.remove('isOpen');
                popupsClosed = true;
            }
            if (
                this.qualityPopup &&
                this.qualityPopup.classList.contains('isOpen')
            ) {
                this.qualityPopup.classList.remove('isOpen');
                popupsClosed = true;
            }

            if (popupsClosed) {
                this.resetHUDTimer();
            }
        }
    };

    public togglePopup = (
        target: HTMLElement | null,
        counterTarget: HTMLElement | null,
    ): void => {
        if (!target) return;
        const isOpen = target.classList.contains('isOpen');
        if (counterTarget) counterTarget.classList.remove('isOpen');

        if (isOpen) {
            target.classList.remove('isOpen');
        } else {
            target.classList.add('isOpen');
        }
        this.resetHUDTimer();
    };

    public handleContainerInteraction = (e: MouseEvent): void => {
        const target = e.target as HTMLElement;

        const hitInteractiveControl =
            target.closest('.Animation_btnView') ||
            target.closest('.Animation_popup') ||
            target.closest('.Animation_bottomSheet') ||
            target.closest('.Animation_editInfo');

        if (hitInteractiveControl) {
            this.resetHUDTimer();
            return;
        }

        if (!this.hasTrueHover) {
            const isHudVisible =
                this.container?.classList.contains('isHudVisible');

            if (isHudVisible) {
                this.container?.classList.remove('isHudVisible');
                if (
                    this.progressBar &&
                    this.progressBar.classList.contains('isLoaded')
                ) {
                    this.progressBar.classList.remove('isVisible');
                }
            } else {
                if (this.container)
                    this.container.classList.add('isHudVisible');
                if (
                    this.progressBar &&
                    this.progressBar.classList.contains('isLoaded')
                ) {
                    this.progressBar.classList.add('isVisible');
                }
            }
        } else {
            this.resetHUDTimer();
        }
    };

    public handleCopy = async (): Promise<void> => {
        if (!this.editInfoText) return;
        try {
            await navigator.clipboard.writeText(this.editInfoText.innerText);
            if (this.editInfoTooltip) {
                this.editInfoTooltip.innerText = 'Kopiert!';
                setTimeout(() => {
                    if (this.editInfoTooltip)
                        this.editInfoTooltip.innerText = 'Klicken zum Kopieren';
                }, 2000);
            }
        } catch (err) {
            notify.error('Fehler beim Kopieren.');
        }
    };

    public handleTouchStart = (e: TouchEvent): void => {
        if (this.engine.isPlaying) return;
        this.touchStartX = e.touches[0].clientX;
    };

    public handleTouchMove = (e: TouchEvent): void => {
        if (this.engine.isPlaying) return;

        const currentX = e.touches[0].clientX;
        const deltaX = this.touchStartX - currentX;

        if (Math.abs(deltaX) > 10) {
            if (e.cancelable) {
                e.preventDefault();
            }
        }
    };

    public handleTouchEnd = (e: TouchEvent): void => {
        if (this.engine.isPlaying) return;

        const deltaX = this.touchStartX - e.changedTouches[0].clientX;
        const deltaThreshold = 40;

        if (Math.abs(deltaX) > deltaThreshold) {
            if (deltaX > 0) {
                this.handleNext();
            } else {
                this.handlePrev();
            }
        }
    };

    public openBottomSheet = (): void => {
        if (!this.bottomSheet) return;
        this.bottomSheet.classList.add('isOpen');
        this.resetBottomSheetPanels();
    };

    public closeBottomSheet = (): void => {
        if (!this.bottomSheet) return;
        this.bottomSheet.classList.remove('isOpen');
        this.container?.classList.remove('isHudVisible'); // Hide UI wrap on execution select actions
    };

    public resetBottomSheetPanels = (): void => {
        this.bsMainPanel?.classList.add('isActive');
        this.bsFocusPanel?.classList.remove('isActive');
        this.bsFpsPanel?.classList.remove('isActive');
        this.bsQualityPanel?.classList.remove('isActive');
    };

    public setupPopupOptions(
        popupEl: HTMLElement | null,
        callback: (val: string) => void,
    ): void {
        popupEl
            ?.querySelectorAll('.Animation_popupOption')
            .forEach((option) => {
                option.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const selection =
                        (option as HTMLElement).dataset.value || 'auto';

                    popupEl
                        .querySelectorAll('.Animation_popupOption')
                        .forEach((opt) => opt.classList.remove('isActive'));
                    option.classList.add('isActive');

                    callback(selection);
                    popupEl.classList.remove('isOpen');
                });
            });
    }

    public syncPlaybackPostResolutionChange = (modeType: string): void => {
        // Handle Auto-Resume if it was playing prior to the change
        if (this.userWantsToPlay) {
            if (!this.engine.isPlaying) {
                this.handleToggle();
            }
        } else {
            // Force strict paused layouts consistently across all cached elements
            if (this.playBtn) {
                this.playBtn.classList.remove('isPlaying');
                this.playBtn.innerHTML =
                    '<span class="Icon Icon-caret"></span>';
            }
            if (this.mobilePlayOverlayBtn) {
                this.mobilePlayOverlayBtn.innerHTML =
                    '<span class="Icon Icon-caret"></span>';
            }
            const buttonPairEl = document.getElementById(
                `ButtonPair_${this.uniqueId}`,
            );
            if (buttonPairEl && modeType !== 'SLIDESHOW') {
                buttonPairEl.style.display = 'flex';
            }
        }
    };

    public rebindControls(): void {
        const id = this.uniqueId;

        // 1. Refresh the cached DOM pointers to target the active nodes on screen
        this.container = document.getElementById(`AnimationContainer_${id}`);
        this.playBtn = document.getElementById(`PlayToggle_${id}`);
        this.prevBtn = document.getElementById(`PrevFrame_${id}`);
        this.nextBtn = document.getElementById(`NextFrame_${id}`);
        this.controlsHUD = document.getElementById(`AnimationControls_${id}`);
        this.progressBar = document.getElementById(
            `AnimationProgressBar_${id}`,
        );

        // 2. Re-bind the click events cleanly to the new nodes
        const onPlayClick = (e: MouseEvent) => {
            e.stopPropagation();
            this.handleToggle();
        };
        const onPrevClick = (e: MouseEvent) => {
            e.stopPropagation();
            this.handlePrev();
        };
        const onNextClick = (e: MouseEvent) => {
            e.stopPropagation();
            this.handleNext();
        };

        this.playBtn?.addEventListener('click', onPlayClick);
        this.prevBtn?.addEventListener('click', onPrevClick);
        this.nextBtn?.addEventListener('click', onNextClick);
    }

    public destroy(): void {
        if (this.engineTrackerInterval)
            clearInterval(this.engineTrackerInterval);
        if (this.hideTimeout) clearTimeout(this.hideTimeout);
        this.engine.destroy();
    }
}
